// src/pages/Contact.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Wrapper with background image styling - lighter overlay
const ContactWrapper = styled.div`
  background-image: url('/images/contact-bg.jpg'); /* Add a background image */
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  min-height: 100vh;
  padding-top: 80px; /* Space for the fixed navbar */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(240, 249, 255, 0.7); /* Lighter overlay (was 0.9) */
    z-index: 0;
  }
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled(motion.h1)`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: ${props => props.theme.colors.secondary};
    margin: 1rem auto;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  h2 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.8rem;
    position: relative;
    
    &::after {
      content: '';
      display: block;
      width: 50px;
      height: 3px;
      background-color: ${props => props.theme.colors.secondary};
      margin: 0.8rem auto 0;
    }
  }
`;

const InfoItem = styled.div`
  margin-bottom: 1.8rem;
  display: flex;
  align-items: flex-start;
  
  .icon {
    margin-right: 1rem;
    color: ${props => props.theme.colors.secondary};
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    background-color: ${props => props.theme.colors.background};
    border-radius: 50%;
  }
  
  div {
    h3 {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
      color: ${props => props.theme.colors.primary};
    }
    
    p, a {
      color: ${props => props.theme.colors.lightText};
      text-decoration: none;
      font-size: 1.05rem;
      line-height: 1.6;
    }
    
    a:hover {
      color: ${props => props.theme.colors.secondary};
    }
  }
`;

// Styled address component for better formatting
const AddressText = styled.div`
  p {
    margin: 0.2rem 0;
  }
  
  .street {
    font-weight: 500;
  }
  
  .postal-code {
    font-weight: 500;
    color: ${props => props.theme.colors.secondary};
  }
  
  .city-country {
    margin-top: 0.3rem;
  }
`;

const ContactForm = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  h2 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.8rem;
    position: relative;
    
    &::after {
      content: '';
      display: block;
      width: 50px;
      height: 3px;
      background-color: ${props => props.theme.colors.secondary};
      margin: 0.8rem auto 0;
    }
  }
`;

const Form = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${props => props.theme.colors.primary};
    font-weight: 500;
  }
  
  input, textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    font-family: inherit;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.secondary};
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const WhatsAppButton = styled(motion.button)`
  background-color: #25D366; /* WhatsApp green */
  color: white;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: #128C7E; /* Darker WhatsApp green */
  }
  
  &:disabled {
    background-color: #94a3b8;
    cursor: not-allowed;
  }
`;

const EmailButton = styled(motion.button)`
  background-color: ${props => props.theme.colors.secondary};
  color: white;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: ${props => props.theme.colors.primary};
  }
`;

// Enhanced Map container with title
const MapSection = styled.div`
  margin-top: 4rem;
`;

const MapTitle = styled.h2`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.secondary};
    margin: 0.8rem auto 2rem;
  }
`;

const MapContainer = styled.div`
  border-radius: 10px;
  overflow: hidden;
  height: 450px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    
    // Format the message for WhatsApp
    const whatsappMessage = `
*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone || 'Not provided'}
*Message:* ${formData.message}
    `.trim();
    
    // Encode the message for WhatsApp URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // WhatsApp phone number without + sign
    const phoneNumber = '447874290538'; // Format: country code + number without +
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappURL, '_blank');
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    
    // Format the email subject and body
    const subject = `Inquiry from ${formData.name}`;
    const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message}
    `.trim();
    
    // Encode the subject and body for mailto URL
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(emailBody);
    
    // Create mailto URL
    const mailtoURL = `mailto:enquiries@ljagapehc.co.uk?subject=${encodedSubject}&body=${encodedBody}`;
    
    // Open default email client
    window.location.href = mailtoURL;
  };
  
  return (
    <ContactWrapper>
      <ContactContainer>
        <PageTitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Contact Us
        </PageTitle>
        
        <ContactGrid>
          <ContactInfo
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Get in Touch</h2>
            
            <InfoItem>
              <div className="icon">
                <i className="fas fa-phone-alt"></i>
              </div>
              <div>
                <h3>Phone</h3>
                <p>+44 7874 290538</p>
              </div>
            </InfoItem>
            
            <InfoItem>
              <div className="icon">
                <i className="fas fa-envelope"></i>
              </div>
              <div>
                <h3>Email</h3>
                <a href="mailto:enquiries@ljagapehc.co.uk">enquiries@ljagapehc.co.uk</a>
              </div>
            </InfoItem>
            
            <InfoItem>
              <div className="icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <div>
                <h3>Office Address</h3>
                <AddressText>
                  <p>L&J Agape Healthcare Ltd</p>
                  <p className="street">1-3 Market Place</p>
                  <p className="postal-code">WV13 2AA</p>
                  <p className="city-country">Willenhall, United Kingdom</p>
                </AddressText>
              </div>
            </InfoItem>
            
            <InfoItem>
              <div className="icon">
                <i className="fas fa-clock"></i>
              </div>
              <div>
                <h3>Business Hours</h3>
                <p>Monday - Friday: 9:00 AM - 5:00 PM</p>
                <p>24/7 Care Services Available</p>
              </div>
            </InfoItem>
          </ContactInfo>
          
          <ContactForm
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Send Us a Message</h2>
            
            <Form>
              <FormGroup>
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </FormGroup>
              
              <ButtonContainer>
                <WhatsAppButton
                  type="button"
                  onClick={handleWhatsAppSubmit}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fab fa-whatsapp"></i> Send via WhatsApp
                </WhatsAppButton>
                
                <EmailButton
                  type="button"
                  onClick={handleEmailSubmit}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-envelope"></i> Send via Email
                </EmailButton>
              </ButtonContainer>
            </Form>
          </ContactForm>
        </ContactGrid>
        
        {/* Google Maps Embed - Updated to new address */}
        <MapSection>
          <MapTitle>Find Us</MapTitle>
          <MapContainer>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2423.473522736184!2d-2.062886723238645!3d52.57685647209774!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48709a3411b15115%3A0x6713e738c6d17b5f!2s1-3%20Market%20Pl%2C%20Willenhall%20WV13%202AA%2C%20UK!5e0!3m2!1sen!2sus!4v1703884800000!5m2!1sen!2sus" 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="L&J Agape Healthcare - 1-3 Market Place, Willenhall"
            ></iframe>
          </MapContainer>
        </MapSection>
      </ContactContainer>
    </ContactWrapper>
  );
};

export default Contact;